import type { Actuator, Body, Joint, Tendon } from "../../types/scene";
import type { getSceneState } from "../sceneStore";

type JointRecord = Record<string, Joint>;
type BodyRecord = Record<string, Body>;
type TendonJointRef = { joint?: unknown; coef?: unknown };
export type TendonWithJointRefs = Tendon & { joints?: TendonJointRef[] };
export type TendonRecord = Record<string, TendonWithJointRefs>;
type SceneSnapshot = ReturnType<typeof getSceneState>;

export type PositionLikeActuator = Actuator & {
  gainprm?: unknown;
  biasprm?: unknown;
};

const getScalarJointRef = (joint: Joint | null | undefined): number => {
  if (!joint || (joint.kind !== "hinge" && joint.kind !== "slide")) return 0;
  return Number(joint.ref) || 0;
};

export const getJointValuesFromMap = (joints: JointRecord | undefined) => {
  const values: Record<string, number> = {};
  if (!joints) return values;
  Object.values(joints).forEach((joint) => {
    values[joint.name] = joint.kind === "hinge" || joint.kind === "slide" ? joint.ref : 0;
  });
  return values;
};

export const getSceneFixedTendonLengthForActuator = (
  actuator: { tendon?: string; tendonUuid?: string },
  tendons: TendonRecord | undefined,
  joints: JointRecord | undefined
): number | null => {
  if (!tendons || !joints) return null;
  const tendonName = typeof actuator?.tendon === "string" ? actuator.tendon : "";
  const tendonUuid = typeof actuator?.tendonUuid === "string" ? actuator.tendonUuid : null;
  let tendon: TendonWithJointRefs | null = null;
  if (tendonUuid) {
    tendon =
      tendons[tendonUuid] || Object.values(tendons).find((t) => t?.uuid === tendonUuid) || null;
  }
  if (!tendon && tendonName) {
    tendon = Object.values(tendons).find((t) => t && t.name === tendonName) || null;
  }
  if (!tendon || !Array.isArray(tendon.joints) || tendon.joints.length === 0) return null;
  let sum = 0;
  let hasValidTerm = false;
  for (const ref of tendon.joints) {
    const ju = ref?.joint;
    if (typeof ju !== "string") continue;
    const coef = Number(ref?.coef);
    const j = joints[ju];
    const q = getScalarJointRef(j);
    if (!Number.isFinite(coef) || !Number.isFinite(q)) continue;
    sum += coef * q;
    hasValidTerm = true;
  }
  return hasValidTerm ? sum : null;
};

export const getInitialFreeJointPoseFromScene = (sceneState: SceneSnapshot): number[] | null => {
  const jointsMap: JointRecord = sceneState?.joints || {};
  const bodiesMap: BodyRecord = sceneState?.bodies || {};
  const worldBodies = Array.isArray(sceneState?.worldBodies) ? sceneState.worldBodies : [];
  const freeJointCount = Object.values(jointsMap).filter((joint) => joint?.kind === "free").length;
  if (freeJointCount !== 1) {
    return null;
  }
  const visited = new Set<string>();

  const poseForBody = (body: Body): number[] | null => {
    if (!body) return null;
    const pos = Array.isArray(body.position) ? body.position : [0, 0, 0];
    const quat = Array.isArray(body.orientation) ? body.orientation : [1, 0, 0, 0];
    return [
      Number(pos[0]) || 0,
      Number(pos[1]) || 0,
      Number(pos[2]) || 0,
      Number(quat[0]) || 1,
      Number(quat[1]) || 0,
      Number(quat[2]) || 0,
      Number(quat[3]) || 0,
    ];
  };

  const findInSubtree = (bodyUuid: string): number[] | null => {
    if (!bodyUuid || visited.has(bodyUuid)) return null;
    visited.add(bodyUuid);
    const body = bodiesMap[bodyUuid];
    if (!body) return null;

    for (const jointUuid of body.joints || []) {
      const joint = jointsMap[String(jointUuid)];
      if (joint?.kind === "free") return poseForBody(body);
    }

    for (const childUuid of body.children || []) {
      const hit = findInSubtree(String(childUuid));
      if (hit) return hit;
    }

    return null;
  };

  for (const root of worldBodies) {
    const rootUuid = typeof root === "string" ? root : root?.uuid;
    if (!rootUuid) continue;
    const hit = findInSubtree(String(rootUuid));
    if (hit) return hit;
  }

  for (const bodyUuid of Object.keys(bodiesMap)) {
    const hit = findInSubtree(bodyUuid);
    if (hit) return hit;
  }

  return null;
};

const getScalarJointsInBodyOrder = (sceneState: SceneSnapshot): Joint[] => {
  const joints: JointRecord = sceneState?.joints || {};
  const bodies: BodyRecord = sceneState?.bodies || {};
  const worldBodies = Array.isArray(sceneState?.worldBodies) ? sceneState.worldBodies : [];
  const ordered: Joint[] = [];
  const seen = new Set<string>();

  const visit = (bodyUuid: string) => {
    const body = bodies?.[bodyUuid];
    if (!body) return;
    (Array.isArray(body.joints) ? body.joints : []).forEach((jointUuid: string) => {
      if (!jointUuid || seen.has(jointUuid)) return;
      const joint = joints?.[jointUuid];
      if (!joint || (joint.kind !== "hinge" && joint.kind !== "slide")) return;
      ordered.push(joint);
      seen.add(jointUuid);
    });
    (Array.isArray(body.children) ? body.children : []).forEach((childUuid: string) =>
      visit(childUuid)
    );
  };

  worldBodies.forEach((entry) => {
    const bodyUuid = typeof entry === "string" ? entry : entry?.uuid;
    if (bodyUuid) visit(String(bodyUuid));
  });
  Object.values(joints).forEach((joint) => {
    if (!joint?.uuid || seen.has(joint.uuid)) return;
    if (joint.kind !== "hinge" && joint.kind !== "slide") return;
    ordered.push(joint);
    seen.add(joint.uuid);
  });
  return ordered;
};

export const buildCurrentSceneInitialJointMap = (
  xmlString: string,
  sceneState: SceneSnapshot
): Record<string, number> => {
  const map = getJointValuesFromMap(sceneState?.joints || {});
  const scalarJoints = getScalarJointsInBodyOrder(sceneState);
  const xmlScalarJointNames = Array.from(xmlString.matchAll(/<\s*joint\b([^>]*)>/gi))
    .map((match) => {
      const attrs = String(match[1] || "");
      const typeMatch = attrs.match(/\btype\s*=\s*"([^"]*)"/i);
      const type = typeMatch
        ? String(typeMatch[1] || "")
            .trim()
            .toLowerCase()
        : "hinge";
      if (type === "free" || type === "ball") return "";
      const nameMatch = attrs.match(/\bname\s*=\s*"([^"]*)"/i);
      return nameMatch ? String(nameMatch[1] || "").trim() : "";
    })
    .filter(Boolean);

  for (let i = 0; i < Math.min(xmlScalarJointNames.length, scalarJoints.length); i += 1) {
    const joint = scalarJoints[i];
    const value = getScalarJointRef(joint);
    if (!Number.isFinite(value)) continue;
    map[xmlScalarJointNames[i]] = value;
  }
  return map;
};
